import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material"
import FormGroup from "@mui/material/FormGroup"
import ArrowUpIcon from "@assets/icons/arrowUp"
import styles from "./FilterItem.module.css"
import { useState, useEffect } from "react"
import ArrowRightIcon from "@assets/icons/arrowRight"
import get from "lodash/get"

const FilterItem = ({
  filterName,
  filterToBeExpanded,
  setPaginationState,
  filterProperty,
  filterPath,
  paginationState,
  valueTransformFunction,
  dataSource = [],
}) => {
  const [noOfOptionsToShow, setShowNoOfOptionsToShow] = useState(5)
  const [showViewAll, setShowViewAll] = useState(true)
  const [options, setOptions] = useState([])

  // Process data source into options whenever dataSource changes
  useEffect(() => {
    const options = getAllOptions()
    setOptions(options)

    // Reset view all state when data changes
    setShowNoOfOptionsToShow(5)
    setShowViewAll(true)
  }, [dataSource, filterPath])

  const getAllOptions = () => {
    if (!dataSource || dataSource.length === 0) return []

    const temp = new Map()

    try {
      dataSource.forEach((item) => {
        // Handle nested paths properly using lodash get
        let originalValue = get(item, filterPath, undefined)

        // Skip undefined, null, or empty values
        if (
          originalValue === undefined ||
          originalValue === null ||
          originalValue === ""
        ) {
          return
        }

        if (valueTransformFunction && originalValue !== undefined) {
          const transformedValue = valueTransformFunction(originalValue)

          if (transformedValue && !temp.has(transformedValue)) {
            temp.set(transformedValue, originalValue)
          }
        } else if (originalValue !== undefined) {
          // Convert to string for display
          const displayValue = String(originalValue)
          if (!temp.has(displayValue)) {
            temp.set(displayValue, originalValue)
          }
        }
      })
    } catch (error) {
      console.error(`Error processing options for ${filterName}:`, error)
    }

    return Array.from(temp.entries())
  }

  // // Debug logging to help troubleshoot
  // console.log(`Filter ${filterName} options:`, options)
  // console.log(`Filter ${filterName} dataSource:`, dataSource)

  const handleOptionClick = (displayValue, originalValue) => {
    setPaginationState((previous) => {
      return {
        ...previous,
        page: 1,
        [filterProperty]: originalValue,
      }
    })
  }

  return (
    <Accordion
      sx={{
        boxShadow: "none",
        marginBottom: "8px",
        "&.MuiAccordion-root::before": {
          height: "0px",
        },
      }}
      defaultExpanded={
        filterToBeExpanded?.toLowerCase() === filterName?.toLowerCase()
          ? true
          : false
      }
      key={filterToBeExpanded}
    >
      <AccordionSummary
        expandIcon={<ArrowUpIcon className="text-[--primary-blue]" />}
        aria-controls="panel1-content"
        id="panel1-header"
        sx={{
          fontStyle: "normal",
          fontWeight: "700",
          fontSize: "14px",
          lineHeight: "16px",
          letterSpacing: "1.25px",
          color: "var(--text-light-gray)",
          width: "100%",
          display: "flex",
          gap: "16px",
          border: "0px",
          borderBottom: "1px solid var(--border-theme-300)",
          padding: "16px",
          "&.Mui-expanded": {
            border: "none",
          },
        }}
      >
        {filterName}
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: "0",
        }}
      >
        <input
          placeholder="search"
          className={styles.searchBar}
          value={paginationState[filterProperty] || ""}
          onChange={(event) => {
            const value = event.target.value
            if (!value) {
              setPaginationState((previous) => {
                const newState = {
                  ...previous,
                  page: 1,
                }
                delete newState[filterProperty]
                return newState
              })
            } else {
              setPaginationState((previous) => {
                return {
                  ...previous,
                  page: 1,
                  [filterProperty]: event.target.value,
                }
              })
            }
          }}
        ></input>
        <FormGroup>
          {options.length > 0 ? (
            options
              .map(([displayValue, originalValue]) => {
                return (
                  <button
                    key={displayValue}
                    onClick={() =>
                      handleOptionClick(displayValue, originalValue)
                    }
                    className={styles.filterDetailsCheckBoxLabel}
                  >
                    {displayValue}
                  </button>
                )
              })
              .filter((item, index) => {
                return index < noOfOptionsToShow
              })
          ) : (
            <div className={styles.filterDetailsCheckBoxLabel}>
              No options available
            </div>
          )}
        </FormGroup>
        {showViewAll && options?.length > 5 ? (
          <button
            className={styles.viewAll}
            onClick={() => {
              setShowNoOfOptionsToShow(dataSource?.length || 0)
              setShowViewAll(false)
            }}
          >
            View all
            <ArrowRightIcon stroke="var(--primary-blue)" />
          </button>
        ) : null}
      </AccordionDetails>
    </Accordion>
  )
}

export default FilterItem
